import React, { useCallback, useEffect } from 'react';
import { useObservable } from 'react-use-observable';

import { Button } from 'antd';

import type { FormikProps } from 'formik';
import { Field, Form, Formik } from 'formik';
import { FilterActions } from 'v2/components/Shared/FilterActions';
import { FilterPanel } from 'v2/components/Shared/FilterPanel';
import { DateRangePickerFormField } from 'v2/components/Shared/FormFields/DateRangePicker';
import { SelectFormField } from 'v2/components/Shared/FormFields/SelectFormField';
import { SelectProduct } from 'v2/components/Shared/FormFields/SelectProduct';
import { TextFormField } from 'v2/components/Shared/FormFields/TextFormField';
import ordersService from 'v2/services/orders';

import { Icon } from '@eduzz/ui-antd-theme';

import initialValues, {
  CustomerTypes,
  ECustomerSearchTypes,
  getInitialValues,
  InvoiceStatus,
  PaymentTypes
} from './initialValues';
import FilterSchema from './validations';

const Filter = () => {
  useEffect(() => {
    ordersService.loadOrderStatus();
  }, []);

  const [isLoading] = useObservable(() => ordersService.isLoading(), []);

  const [orderStatus] = useObservable(() => ordersService.getOrderStatus(), []);

  const onSubmit = useCallback((values: typeof initialValues, { setSubmitting }: FormikProps<typeof initialValues>) => {
    ordersService.setFilter({ ...values });
    setSubmitting(false);
  }, []);

  return (
    <Formik initialValues={initialValues} validationSchema={FilterSchema} onSubmit={onSubmit}>
      {({ isValid, isSubmitting, dirty, resetForm, values, setFieldValue }: FormikProps<typeof initialValues>) => {
        const onChangeCustomerType = (e: React.ChangeEvent<HTMLInputElement>) => {
          const v = e.target.value;
          setFieldValue('customer', '');
          setFieldValue('customer_type', v);
        };

        const onChangeInvoice = (e: React.ChangeEvent<HTMLInputElement>) => {
          const v = e.target.value;
          let value = null;
          if (v && !isNaN(Number(v))) {
            value = v;
          }
          setFieldValue('invoice_id', value);
        };

        const handleClearFilter = () => {
          const clearedValues = getInitialValues();
          resetForm({ values: clearedValues });
          ordersService.cleanFilter();
        };

        const isCleanDisabled = !dirty || isSubmitting || isLoading;
        const isFilterDisabled = !isValid || isSubmitting || isLoading;

        return (
          <Form id='sales-filter'>
            <FilterPanel id='orders-filter-panel'>
              <div className='grid grid-cols-1 items-end gap-4 sm:grid-cols-2 md:grid-cols-3'>
                <div>
                  <Field label='Período' id='date' name='date' component={DateRangePickerFormField} />
                </div>
                <div>
                  <div className='grid grid-cols-3 items-end gap-2'>
                    <div>
                      <Field
                        id='customer_type'
                        name='customer_type'
                        label='Cliente'
                        fullWidth
                        options={CustomerTypes}
                        component={SelectFormField}
                        onChange={onChangeCustomerType}
                      />
                    </div>
                    <div className='col-span-2'>
                      <Field
                        id='customer'
                        name='customer'
                        component={TextFormField}
                        fullWidth
                        label={
                          values.customer_type == ECustomerSearchTypes.DOCUMENT
                            ? 'CPF/CNPJ'
                            : values.customer_type == ECustomerSearchTypes.EMAIL
                              ? 'E-mail'
                              : 'Nome'
                        }
                        placeholder={
                          values.customer_type == ECustomerSearchTypes.NAME
                            ? 'Insira o nome'
                            : values.customer_type == ECustomerSearchTypes.EMAIL
                              ? 'Insira o e-mail'
                              : 'Insira o CPF/CNPJ'
                        }
                        mask={values.customer_type == ECustomerSearchTypes.DOCUMENT ? 'document' : null}
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <Field
                    type={'tel'}
                    label='Fatura'
                    onChange={onChangeInvoice}
                    fullWidth
                    id='invoice_id'
                    name='invoice_id'
                    mask='number'
                    component={TextFormField}
                  />
                </div>
                <div>
                  <Field
                    id='product_id'
                    name='product_id'
                    fullWidth
                    label='Produtos'
                    endpoint='/reports/filter-physical-products'
                    component={SelectProduct}
                  />
                </div>
                <div>
                  <Field
                    id='payment_status'
                    name='payment_status'
                    multiple
                    options={InvoiceStatus}
                    fullWidth
                    label='Status da fatura'
                    component={SelectFormField}
                  />
                </div>
                <div>
                  <Field
                    id='status'
                    fullWidth
                    multiple
                    name='status'
                    options={orderStatus}
                    label='Status da entrega'
                    component={SelectFormField}
                  />
                </div>
                <div>
                  <Field
                    id='payment_method'
                    fullWidth
                    multiple
                    name='payment_method'
                    options={PaymentTypes}
                    label='Tipo de pagamento'
                    component={SelectFormField}
                  />
                </div>
                <div className='md:col-span-2'>
                  <FilterActions>
                    <Button
                      id='clear-button'
                      disabled={isCleanDisabled}
                      type='text'
                      onClick={handleClearFilter}
                      loading={isLoading || isSubmitting}
                    >
                      <span id='clear-button-label'>Limpar</span>
                    </Button>
                    <Button
                      id='basic-filter-button'
                      type='primary'
                      disabled={isFilterDisabled}
                      htmlType='submit'
                      loading={isLoading || isSubmitting}
                      icon={<Icon.Funnel id='basic-filter-button-icon' />}
                    >
                      <span id='basic-filter-button-label'>Filtrar</span>
                    </Button>
                  </FilterActions>
                </div>
              </div>
            </FilterPanel>
          </Form>
        );
      }}
    </Formik>
  );
};

export default Filter;
